import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpResponse, HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, from, lastValueFrom, EMPTY, switchMap, filter, map, catchError, of } from "rxjs";
import { AuthRequestService } from "src/app/auth/auth-request.service";
import { AuthService } from "src/app/auth/auth.service";
import { AccountService } from "src/openapi-gen/services";
import { NavRoutes } from "../constants/nav.routes";
import { ErrorRouter } from "src/app/shared/services/error-router";
import { Router } from "@angular/router";

@Injectable()
export class AuthHttpRequestInterceptor implements HttpInterceptor {
    private static isRefreshing: boolean = false;
    
    constructor(
        private router: Router,
        private errorRouter: ErrorRouter,
        private accountService: AccountService,
        private authRequestService: AuthRequestService,
        private authService: AuthService) { }

    /**
     * This is the point where all api requests will funnel through before hitting the server.
     * There are multiple scenarios which can occurr.
     * 
     * 1) The request is configured to bypass error redirection meaning the original caller of the request needs to handle api errors manually.
     * 2) The request is configured to be anonymous meaning we can make the request under normal circumstances (api errors occurring will have a user redirected).
     * 3) The request is configured to require authentication meaning if an access token is available the request can be made under normal circumstances.
     * If an access token is not available the credentials of a user must first be refreshed before the original request can be made.
     * If no refresh token is available the user must be redirected to the login screen.
     * 
     * @param request The incoming request caught by the interceptor
     * @param next The handler which allows execution of the incoming request when needed
     * @returns An observable (data over time) representing the HttpEvent
     */
    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        console.debug('Auth Interceptor Begin: ', request.url);

        request = request.clone({
            withCredentials: true
        });

        return from(this.handleAuthRequest(request, next)); // The 'from' operator allows the creation of an observable from a promise
    }

    /**
     * The primary purpose of this method is to delay subsequent requests when a refresh is in progress to
     * prevent multiple instances of a credential refresh from occurring.
     * 
     * @param request The incoming request caught by the interceptor
     * @param next The handler which allows execution of the incoming request when needed
     * @returns A promise representing the completion of an Http Request
     */
    async handleAuthRequest(request: HttpRequest<any>, next: HttpHandler): Promise<any> {
        if(this.authRequestService.isRequestAnonymous(request.url)) {
            return await lastValueFrom(this.handleOriginalRequest(request, next));
        }

        if(!this.authRequestService.areTokensAvailable() || !this.authRequestService.isRefreshTokenActive()) {
            AuthService.SetUserLoggedOut();
            this.router.navigate([NavRoutes.Root]);
            return EMPTY;
        }

        if (AuthHttpRequestInterceptor.isRefreshing) {
            return await lastValueFrom(await this.delayIncomingRequest(request, next));
        }
        else {
            if(this.authRequestService.isAccessTokenRefreshNeeded()) {
                AuthHttpRequestInterceptor.setTokenRefreshing();
                await lastValueFrom(this.refreshAccessToken());

                console.debug('Now making original request for', request.url, request.method);
                return await lastValueFrom(this.handleOriginalRequest(request, next));
            }
            else {
                return await lastValueFrom(this.handleOriginalRequest(request, next));
            }
        }
    }

    /**
     * The interceptor will wait until the refresh token api call has completed before making
     * the original request. Once the refresh token api call is made it will be intercepted
     * by another instance of the auth interceptor. This means if an error occurs during
     * refresh since the request will be treated as a regular request the app will redirect
     * a user to the unauthorized page cancelling all current requests completely.
     * 
     * @param request The incoming request caught by the interceptor
     * @param next The handler which allows execution of the incoming request when needed
     * @returns An observable (represents data over time) of the refresh token request to
     * be executed by the handling caller. 
     */
    private refreshAccessToken(): Observable<any> {
        console.debug('Attempting to refresh access token before making original request');
        return this.accountService.apiAccountRefreshTokenGet().pipe(
            switchMap((tokenData) => {
                this.authService.setStorageData(tokenData);
                AuthHttpRequestInterceptor.clearTokenRefreshing();

                console.debug('Access token refresh success!');
                return new Promise(resolve => setTimeout(resolve, 10));
            })
        );
    }

    private async delayIncomingRequest(request: HttpRequest<any>, next: HttpHandler): Promise<Observable<any>> {
        while(AuthHttpRequestInterceptor.isRefreshing) {
            console.debug('Delay active for: ', request.url);
            await this.delay(1000); // 1 second
        }

        console.debug('Refresh completed. Making request for: ', request.url);
        return this.handleOriginalRequest(request, next);
    }

    private handleOriginalRequest(request: HttpRequest<any>, next: HttpHandler): Observable<any> {
        console.debug('Making request for: ', request.url);

        // These requests are not applicable for authorization
        if(this.authRequestService.bypassErrorRedirection(request.url)) {
            console.debug('By passing error redirection.');
            return next.handle(request);
        }

        return next
        .handle(request)
        .pipe(
            filter(event => event instanceof HttpResponse),
            map((successResponse: HttpResponse<any>) => {
                return successResponse;
            }),
            catchError((errorResponse: HttpErrorResponse) => {
                AuthHttpRequestInterceptor.clearTokenRefreshing();
                if(errorResponse.status === 0) {
                    this.errorRouter.navigateToServerConnectionError();
                }
                else if (errorResponse.status === 401 || errorResponse.status === 403) {
                    if (AuthService.IsUserLoggedIn()) {
                        AuthService.SetUserLoggedOut();
                        this.errorRouter.navigateToUnauthorized();
                    }
                }
                else if (errorResponse.status === 404) {
                    this.errorRouter.navigateToNotFound();
                }
                else {
                    this.errorRouter.navigateToInternalError();
                }

                return of(errorResponse);
            })
        );
    }

    private static setTokenRefreshing() {
        AuthHttpRequestInterceptor.isRefreshing = true;
    }

    private static clearTokenRefreshing() {
        AuthHttpRequestInterceptor.isRefreshing = false;
    }

    private async delay(ms: number) {
        return await new Promise(resolve => setTimeout(resolve, ms));
    }
}